(() => {
  if (customElements.get('vr-shipping-calculator')) return;
  const cache = new Map();
  const createQuery = `mutation($input:CartInput!){cartCreate(input:$input){cart{id totalQuantity lines(first:2){nodes{quantity merchandise{... on ProductVariant{id}}}}} userErrors{message}}}`;
  const ratesQuery = `query($id:ID!){cart(id:$id){... @defer {deliveryGroups(first:10,withCarrierRates:true){nodes{deliveryOptions{title estimatedCost{amount currencyCode}}}}}}}`;

  // Shopify returns carrier rates in a deferred multipart response.
  function parseResponse(text, contentType) {
    if (!contentType.includes('multipart/mixed')) return [JSON.parse(text)];
    const match = contentType.match(/boundary="?([^";\s]+)/i);
    const boundary = match ? match[1] : 'graphql';
    return text.split('--' + boundary).flatMap(part => {
      const start = part.indexOf('{'), end = part.lastIndexOf('}');
      return start < 0 || end < start ? [] : [JSON.parse(part.slice(start, end + 1))];
    });
  }
  async function graphql(query, variables, signal) {
    const response = await fetch('/api/2026-10/graphql.json', {
      method: 'POST', credentials: 'omit', signal,
      headers: {'Content-Type':'application/json', 'Accept':'multipart/mixed; deferSpec=20220824, application/json'},
      body: JSON.stringify({query, variables})
    });
    if (!response.ok) throw new Error('service');
    const packets = parseResponse(await response.text(), response.headers.get('content-type') || '');
    if (!packets.length || packets.some(p => p.errors?.length || p.incremental?.some(i => i.errors?.length))) throw new Error('service');
    return packets;
  }
  function deliveryGroups(packets) {
    for (const packet of packets) {
      if (packet.data?.cart?.deliveryGroups) return packet.data.cart.deliveryGroups.nodes;
      for (const increment of packet.incremental || []) {
        if (increment.data?.deliveryGroups) return increment.data.deliveryGroups.nodes;
      }
    }
    return [];
  }

  customElements.define('vr-shipping-calculator', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.root = this.closest('product-info');
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form');
      this.result = this.querySelector('.vr-shipping-result');
      this.submit = this.form.querySelector('[type="submit"]');
      this.country = this.form.elements.country;
      this.usaSmartShipping = this.querySelector('[data-usa-smart-shipping]');
      this.zip = this.form.elements.zip;
      this.it = this.dataset.language.startsWith('it');
      this.summary = this.querySelector('.vr-shipping-summary');
      this.customsSection = this.querySelector('.vr-shipping-customs-section');
      this.resetQuote();
      this.renderPackaging();
      this.timing = this.querySelector('.vr-shipping-timing');
      this.renderTiming();
      this.onRootClick = event => {
        const opener = event.target.closest('.vr-shipping-estimate');
        if (!opener) return;
        this.opener = opener;
        this.renderTiming();
        this.resetQuote();
        this.dialog.showModal();
        this.zip.focus();
      };
      this.root.addEventListener('click', this.onRootClick);
      this.querySelector('[data-close]').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
      });
      this.dialog.addEventListener('close', () => {
        this.controller?.abort(); this.sequence = (this.sequence || 0) + 1;
        this.submit.disabled = false; this.form.removeAttribute('aria-busy');
        this.opener?.focus();
      });
      this.form.addEventListener('submit', event => {event.preventDefault(); this.calculate();});
      for (const input of [this.country, this.zip]) input.addEventListener('input', () => {
        this.zip.setCustomValidity(''); this.resetQuote();
        this.controller?.abort(); this.sequence = (this.sequence || 0) + 1;
        this.submit.disabled = false; this.form.removeAttribute('aria-busy');
        this.renderTiming();
      });
    }
    resetQuote() {
      this.summary.dataset.state = 'empty';
      this.updateUsaSmartShipping();
      this.result.textContent = this.it ? 'Inserisci nazione e CAP per conoscere il costo.' : 'Enter a country and postal code to get a quote.';
    }
    updateUsaSmartShipping() {
      if (this.usaSmartShipping) this.usaSmartShipping.hidden = this.country.value !== 'US' || this.summary.dataset.state !== 'ready';
    }
    renderPackaging() {
      const row = this.querySelector('.vr-shipping-packaging');
      if (!row) return;
      const text = (this.dataset.packagingDescription || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const marker = /dimension[ei]\s+(?:dell['’]?\s*)?imball(?:o|aggio)\s*[:\-]?/i.exec(text);
      const block = marker ? text.slice(marker.index + marker[0].length) : '';
      const number = label => {
        const match = new RegExp(label + '\\s*:?\\s*(\\d+(?:[.,]\\d+)?)', 'i').exec(block);
        return match ? Number(match[1].replace(',', '.')) : null;
      };
      let dimensions = [number('Lunghezza'), number('(?:Profondita|Larghezza)'), number('Altezza')];
      if (!dimensions.every(value => Number.isFinite(value) && value > 0)) {
        const compact = /(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)/i.exec(block);
        dimensions = compact ? compact.slice(1).map(value => Number(value.replace(',', '.'))) : [];
      }
      if (dimensions.length !== 3 || !dimensions.every(value => Number.isFinite(value) && value > 0)) {
        row.textContent = this.it ? 'Dimensioni dell’imballo disponibili su richiesta.' : 'Packaging dimensions available on request.';
        return;
      }
      const format = new Intl.NumberFormat(this.dataset.language || 'it', {maximumFractionDigits: 3});
      const size = dimensions.map(value => format.format(value)).join(' × ');
      const volume = format.format(dimensions.reduce((total, value) => total * value, 1) / 1000000);
      row.textContent = this.it
        ? `Imballo: ${size} cm · Volume: ${volume} m³`
        : `Packaging: ${size} cm · Volume: ${volume} m³`;
    }
    shippingTiming() {
      const country = this.country.value;
      const zip = this.zip.value.trim().replace(/\s/g, '');
      const eu = new Set('AT BE BG HR CY CZ DK EE FI FR DE GR HU IE IT LV LT LU MT NL PL PT RO SK SI ES SE'.split(' '));
      const europe = new Set('AL AD AM AZ BY BA FO GE GI GG IS IM JE XK LI MD MC ME MK NO RU SM RS CH TR UA VA AX'.split(' '));
      const specialTerritory = (country === 'ES' && /^(35|38|51|52)/.test(zip))
        || (country === 'IT' && ['23041','22061'].includes(zip))
        || (country === 'DE' && ['27498','78266'].includes(zip));
      return {
        transit: ['US','GB'].includes(country) ? 6 : (eu.has(country) || europe.has(country) ? 4 : 10),
        customs: specialTerritory || (!eu.has(country) && !['MC','SM'].includes(country))
      };
    }
    renderTiming() {
      const {transit, customs} = this.shippingTiming();
      this.timing.replaceChildren();
      const heading = document.createElement('h3');
      heading.textContent = this.it ? `A casa tua in circa ${5 + transit} giorni lavorativi` : `At your door in around ${5 + transit} business days`;
      const preparation = document.createElement('p');
      preparation.className = 'vr-shipping-detail';
      preparation.textContent = this.it ? 'Preparazione imballo: 5 giorni lavorativi.' : 'Packaging preparation: 5 business days.';
      const journey = document.createElement('p');
      journey.className = 'vr-shipping-detail';
      journey.textContent = this.it ? `Transito: ${transit} giorni lavorativi.` : `Transit: ${transit} business days.`;
      this.timing.append(heading, preparation, journey);
      this.customsSection.replaceChildren();
      this.customsSection.hidden = !customs;
      if (customs) {
        const customsHeading = document.createElement('h3');
        customsHeading.textContent = this.it ? 'Spedizioni extra UE - tasse alla dogana escluse' : 'Shipping outside the EU - customs duties and taxes excluded';
        const deliveryTerms = document.createElement('p'); deliveryTerms.className = 'vr-shipping-detail';
        const dap = document.createElement('strong'); dap.textContent = 'DAP – Delivered At Place';
        deliveryTerms.append(
          this.it ? 'Per le spedizioni verso paesi al di fuori dell’Unione Europea applichiamo la resa ' : 'For shipments to countries outside the European Union, we apply ',
          dap,
          this.it ? ' (Incoterms® 2020): ci occupiamo del trasporto e ne sosteniamo costi e rischi fino alla consegna all’indirizzo di destinazione.' : ' terms (Incoterms® 2020): we arrange transport and bear its costs and risks until delivery to the destination address.'
        );
        const charges = document.createElement('p'); charges.className = 'vr-shipping-detail';
        const excluded = document.createElement('strong'); excluded.textContent = this.it ? 'non comprende' : 'does not include';
        charges.append(
          this.it ? 'Il costo di spedizione ' : 'The shipping cost ',
          excluded,
          this.it ? ' dazi, IVA all’importazione e altri oneri doganali previsti dal paese di destinazione, che restano a carico del destinatario. Al momento dello sdoganamento, il corriere vi contatterà direttamente per il pagamento degli eventuali importi dovuti, necessario per completare la consegna.' : ' import duties, import VAT or other customs charges imposed by the destination country, which remain payable by the recipient. During customs clearance, the carrier will contact you directly to collect any amounts due, which must be paid to complete delivery.'
        );
        const note = document.createElement('p'); note.className = 'vr-shipping-detail';
        note.textContent = this.it ? 'LA STIMA NON INCLUDE EVENTUALI RITARDI DOGANALI.' : 'THE ESTIMATE EXCLUDES POSSIBLE CUSTOMS DELAYS.';
        this.timing.append(note);
        this.customsSection.append(customsHeading, deliveryTerms, charges);
      }
    }
    disconnectedCallback() {this.controller?.abort(); this.root?.removeEventListener('click', this.onRootClick);}
    async calculate() {
      const zip = this.zip.value.trim().toUpperCase(), country = this.country.value;
      this.zip.setCustomValidity('');
      if (country === 'IT' && !/^[0-9]{5}$/.test(zip)) {
        this.zip.setCustomValidity(this.it ? 'Inserisci un CAP di 5 cifre.' : 'Enter a 5-digit postal code.');
      }
      if (!this.form.reportValidity()) return;
      const variantId = this.root.querySelector('product-form input[name="id"]')?.value || this.dataset.variantId;
      if (!variantId) return;
      const merchandiseId = 'gid://shopify/ProductVariant/' + variantId;
      const key = [variantId, country, zip].join('|');
      const cached = cache.get(key);
      if (cached && Date.now() - cached.at < 300000) {this.renderRates(cached.options); return;}
      const sequence = this.sequence = (this.sequence || 0) + 1;
      this.controller?.abort(); this.controller = new AbortController();
      const signal = this.controller.signal;
      const timeout = setTimeout(() => this.controller?.abort(), 45000);
      this.submit.disabled = true; this.form.setAttribute('aria-busy', 'true');
      this.summary.dataset.state = 'loading';
      this.updateUsaSmartShipping();
      this.result.textContent = this.it ? 'Calcolo della spedizione…' : 'Calculating shipping…';
      try {
        // A separate Storefront cart: no AJAX cart mutations, cookie changes, or customer identity.
        const packets = await graphql(createQuery, {input:{
          lines:[{merchandiseId, quantity:1}], buyerIdentity:{countryCode:country},
          delivery:{addresses:[{address:{deliveryAddress:{countryCode:country, zip}}, selected:true, oneTimeUse:true}]}
        }}, signal);
        const payload = packets[0].data?.cartCreate;
        if (!payload || payload.userErrors?.length) throw new Error('unavailable');
        const cart = payload.cart, lines = cart?.lines?.nodes;
        if (cart?.totalQuantity !== 1 || lines?.length !== 1 || lines[0].quantity !== 1 || lines[0].merchandise.id !== merchandiseId) throw new Error('unavailable');
        let groups = [];
        for (let attempt = 0; attempt < 2; attempt++) {
          groups = deliveryGroups(await graphql(ratesQuery, {id:cart.id}, signal));
          if (groups.length) break;
        }
        // Do not sum unrelated delivery methods if Shopify splits a shipment into multiple groups.
        if (groups.length !== 1) throw new Error('unavailable');
        const options = groups[0].deliveryOptions.filter(o => Number.isFinite(Number(o.estimatedCost.amount)) && Number(o.estimatedCost.amount) >= 0);
        if (!options.length) throw new Error('unavailable');
        if (sequence !== this.sequence || !this.dialog.open) return;
        cache.set(key, {at:Date.now(), options}); this.renderRates(options);
      } catch (error) {
        if (sequence !== this.sequence || !this.dialog.open) return;
        this.summary.dataset.state = 'error';
        this.updateUsaSmartShipping();
        this.result.textContent = error.message === 'unavailable'
          ? (this.it ? 'Per questo prodotto non è disponibile una quotazione automatica. Contattaci per verificare i costi di spedizione.' : 'An automatic shipping quote is not available for this product. Contact us to check shipping costs.')
          : (this.it ? 'Non riusciamo a calcolare la spedizione in questo momento. Riprova tra poco.' : 'We cannot calculate shipping right now. Please try again shortly.');
      } finally {
        clearTimeout(timeout);
        if (sequence === this.sequence) {this.submit.disabled = false; this.form.removeAttribute('aria-busy');}
      }
    }
    renderRates(options) {
      this.summary.dataset.state = 'ready';
      this.updateUsaSmartShipping();
      this.result.replaceChildren();
      for (const option of options) {
        const row = document.createElement('div'); row.className = 'vr-shipping-option';
        const title = document.createElement('span'); title.textContent = option.title;
        const price = document.createElement('strong');
        price.textContent = new Intl.NumberFormat(this.dataset.language || 'it', {style:'currency', currency:option.estimatedCost.currencyCode}).format(Number(option.estimatedCost.amount));
        row.append(price);
        if (options.length > 1) row.append(title);
        this.result.append(row);
      }
    }
  });
})();
